import { Crown, Shield, ShieldCheck, User, Eye, type LucideIcon } from 'lucide-react';
import type { Role, ClanScopedRole, OrganizationRole } from './api';

export function isAdminRole(role?: Role | null): boolean {
  return role === 'founder' || role === 'admin';
}

export function canWrite(role?: Role | null): boolean {
  return role === 'founder' || role === 'admin';
}

// Gate for "can this user self-serve register their clan into a competition"
// — the register-my-clan flow (JoinCompScreen, POST .../register-my-clan) is
// leader-gated server-side and needs a clan to act on. A CTA that opens that
// door for anyone else (member/admin/founder/no-clan) either 403s or dead-ends
// on JoinCompScreen's "build a clan first" screen — so every doorway into it
// (Sidebar, LeaderPanel, MyCompDetail, JoinCompModal) shares this ONE check
// rather than re-deriving it per surface (registration-hole closure, 2026-07-17).
export function canRegisterClan(role?: Role | null, clanName?: string | null): boolean {
  return role === 'leader' && !!clanName;
}

export type RoleMeta = {
  label: string;
  icon: LucideIcon;
  tone: string;
  ring: string;
  text: string;
  dot: string;
  blurb: string;
};

// Maps a clan-scoped role to a display label — single role spine rewire
// (2026-07-24): CLAN_OWNER is the clan head, now labelled 'Clan Leader'
// (was 'Leader'). CLAN_LEADER is the deputy, now labelled 'Clan Captain'
// (was 'Co-Admin') — this INVERTS the old naming, where 'Leader' meant the
// head and 'Co-Admin' meant the deputy; don't let the enum names (CLAN_OWNER/
// CLAN_LEADER) mislead you into reading these backwards.
export function clanRoleDisplayName(scopedRole: ClanScopedRole): string {
  switch (scopedRole) {
    case 'CLAN_OWNER': return 'Clan Leader';
    case 'CLAN_LEADER': return 'Clan Captain';
    case 'CLAN_MEMBER': return 'Member';
  }
}

// Maps an org-membership role to a clean Title-Case display label — the single
// authority (mirrors clanRoleDisplayName), replacing the scattered
// `role.replace('ORG_', '')` hacks that rendered inconsistently (lowercase in
// some panels, uppercase in others). 'Org Owner' here is the org head; distinct
// from a clan's 'Clan Leader'. ORG_VIEWER folds into Member on the single role
// spine (2026-07-24) — no distinct 'Viewer' tier any more.
export function orgRoleDisplayName(role: OrganizationRole): string {
  switch (role) {
    case 'ORG_OWNER': return 'Org Owner';
    case 'ORG_ADMIN': return 'Admin';
    case 'ORG_MEMBER': return 'Member';
    case 'ORG_VIEWER': return 'Member';
  }
}

// Produces a short badge label for a member's team assignment.
// Returns null when the member is benched (no team).
export function teamBadgeLabel(teamNo: number | null | undefined): string | null {
  return teamNo ? `Team ${teamNo}` : null;
}

// Produces the scope label for a captain delegation row.
// e.g. "Alpha Squad", "Team 3", or "Clan-wide".
export function captainLabel(teamNo?: number | null, teamName?: string): string {
  return teamName?.trim() || (teamNo ? `Team ${teamNo}` : 'Clan-wide');
}

// User-facing naming for the three delegation kinds (GOD-policy L6,
// 2026-07-21; COMP_SCOPED added same day — Lisa's ruling: a captain for the
// duration of ONE competition, ending automatically when it does). The
// backend enum stays mechanical ('PERMANENT'/'WINDOWED'/'COMP_SCOPED') — this
// is the ONLY place the player-facing copy lives, so every surface (modal,
// badge, captains list) says the same thing. Absent kind (legacy rows) reads
// as 'WINDOWED', matching the backend's dual-state default.
export type CaptainGrantKind = 'WINDOWED' | 'PERMANENT' | 'COMP_SCOPED';

export function captainKindLabel(kind?: CaptainGrantKind | null): string {
  if (kind === 'PERMANENT') return 'Team Captain';
  if (kind === 'COMP_SCOPED') return 'Comp captain (until comp ends)';
  return 'Stand-in captain (time-boxed)';
}

// Short badge form for inline chips — same distinction, terser.
export function captainKindBadge(kind?: CaptainGrantKind | null): string {
  if (kind === 'PERMANENT') return 'Captain';
  if (kind === 'COMP_SCOPED') return 'Comp Captain';
  return 'Stand-in';
}

// ── Captain expiry display — SINGLE gate for "what expiry note, if any, does
//    this captain's badge show" (CaptainsPanel.tsx / ClanTeamsBoard.tsx both
//    delegate here instead of re-deriving the same `kind !== 'PERMANENT' &&
//    typeof expiresAt === 'number'` condition inline). Pure — the caller
//    supplies `expiresAt` (already resolved by the API response) and turns a
//    'countdown' result into text itself (relativeExpiry, Date.now()-based,
//    stays impure and local to each component).
//  • PERMANENT              -> 'none'            (never expires, no note)
//  • COMP_SCOPED             -> 'until-comp-ends'  (no countdown — see header)
//  • WINDOWED (or absent kind) with a numeric expiresAt -> 'countdown'
//  • anything else (WINDOWED with no usable expiresAt) -> 'none'
export type CaptainExpiryDisplay =
  | { kind: 'none' }
  | { kind: 'until-comp-ends' }
  | { kind: 'countdown'; expiresAt: number };

export function captainExpiryDisplay(
  grantKind: CaptainGrantKind | null | undefined,
  expiresAt: number | null | undefined,
): CaptainExpiryDisplay {
  if (grantKind === 'PERMANENT') return { kind: 'none' };
  if (grantKind === 'COMP_SCOPED') return { kind: 'until-comp-ends' };
  if (typeof expiresAt === 'number') return { kind: 'countdown', expiresAt };
  return { kind: 'none' };
}

// ── Comp-scoped appointment picker — auto-select when the clan has exactly
//    ONE active competition enrollment (no need to make the leader pick from
//    a list of one); null when zero or multiple (leader must choose, or the
//    option is disabled entirely when zero — see MakeCaptainModal). Pure —
//    the caller supplies whatever "active enrollments" list it already
//    fetched (api.publicClanPage(clanName).comps, LIVE/PENDING only).
export function autoSelectSoleComp(comps: Array<{ compId: string }>): string | null {
  return comps.length === 1 ? comps[0].compId : null;
}

export const ROLE_META: Record<Role, RoleMeta> = {
  founder: {
    label: 'Founder',
    icon: ShieldCheck,
    tone: 'bg-red-500/15',
    ring: 'ring-red-500/40',
    text: 'text-red-300',
    dot: 'bg-red-400',
    blurb: 'Platform-level. Debug tools on.',
  },
  admin: {
    label: 'Platform Admin',
    icon: Shield,
    tone: 'bg-forge-500/15',
    ring: 'ring-forge-500/40',
    text: 'text-forge-300',
    dot: 'bg-forge-400',
    blurb: 'Runs orgs and competitions.',
  },
  leader: {
    label: 'Clan Leader',
    icon: Crown,
    tone: 'bg-emerald-500/15',
    ring: 'ring-emerald-500/40',
    text: 'text-emerald-300',
    dot: 'bg-emerald-400',
    blurb: 'Captains a clan. Runs the roster.',
  },
  member: {
    label: 'Member',
    icon: User,
    tone: 'bg-iron-500/20',
    ring: 'ring-iron-500/40',
    text: 'text-iron-300',
    dot: 'bg-iron-400',
    blurb: 'Plays in a clan.',
  },
  public: {
    label: 'Public',
    icon: Eye,
    tone: 'bg-iron-700/30',
    ring: 'ring-iron-600/40',
    text: 'text-iron-400',
    dot: 'bg-iron-500',
    blurb: 'Spectator view.',
  },
};
